import type { KeyPersonRoleType } from '@commenda/api';
import { CONTROL_TYPE_OPTIONS } from '../../OwnershipV2/constants';
import type { RoleOption } from './types';

export const ROLE_NOUN: Record<KeyPersonRoleType, string> = {
  DIRECTOR: 'director',
  OFFICER: 'officer',
  GOVERNANCE: 'governance',
  AUTHORIZED_SIGNATORY: 'authorized signatory',
  UBO: 'UBO',
};

// Label for the value a role carries (e.g. an officer's title), shown read-only when editing.
export const ROLE_DETAIL_LABEL: Record<KeyPersonRoleType, string> = {
  DIRECTOR: 'Role',
  OFFICER: 'Officer role',
  GOVERNANCE: 'Governance role',
  AUTHORIZED_SIGNATORY: 'Role',
  UBO: 'Control type',
};

export const ROLE_DESCRIPTIONS: Record<KeyPersonRoleType, string> = {
  DIRECTOR: "Serves on the entity's board of directors.",
  OFFICER: 'A formal corporate office (e.g. CEO, CFO, Secretary).',
  GOVERNANCE: 'Holds a governance position such as Manager or Member.',
  AUTHORIZED_SIGNATORY:
    'Can sign contracts and other binding documents on behalf of the entity.',
  UBO: 'Ultimately owns or controls 25% or more of the entity.',
};

// Fuller titles for the role picker (presentation copy; the compact bucket labels come from the registry).
export const ROLE_CARD_TITLE: Record<KeyPersonRoleType, string> = {
  DIRECTOR: 'Director',
  OFFICER: 'Officer role',
  GOVERNANCE: 'Governance',
  AUTHORIZED_SIGNATORY: 'Authorized Signatory',
  UBO: 'Ultimate Beneficial Owner (UBO)',
};

// Values/labels must match the seeded AttributePropertyOption rows exactly or
// assignments fail with OPTION_NOT_ALLOWED.
export const OFFICER_ROLE_OPTIONS: RoleOption[] = [
  { value: 'President', label: 'President' },
  { value: 'Secretary', label: 'Corporate Secretary' },
  { value: 'Treasurer', label: 'Treasurer' },
  { value: 'VicePresident', label: 'Vice President' },
  { value: 'CEO', label: 'CEO' },
  { value: 'CFO', label: 'CFO' },
  { value: 'COO', label: 'COO' },
  { value: 'CTO', label: 'CTO' },
  { value: 'CMO', label: 'CMO' },
  { value: 'CIO', label: 'CIO' },
  { value: 'GeneralCounsel', label: 'General Counsel' },
  { value: 'Chairman', label: 'Chairman' },
  { value: 'ManagingDirector', label: 'Managing Director' },
];

export const GOVERNANCE_ROLE_OPTIONS: RoleOption[] = [
  { value: 'Director', label: 'Director' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Member', label: 'Member' },
  { value: 'GeneralPartner', label: 'General Partner' },
];

// The ownership tab's wording is authoritative; both surfaces write the same
// UBO assignment, so they can't describe its control type differently.
export const UBO_CONTROL_TYPE_OPTIONS: RoleOption[] = CONTROL_TYPE_OPTIONS;

// Human-readable reasons for per-item assign outcomes; raw engine codes are opaque to users.
export const ASSIGN_RESULT_MESSAGE: Record<string, string> = {
  INVALID_DATE: 'the end date is before the start date',
  OFFICER_ROLE_ALREADY_HELD: 'that officer role is already assigned',
  SINGLE_ACTIVE_VIOLATION: 'an active role of this type already exists',
  OPTION_NOT_ALLOWED: 'a selected option is not allowed',
  HOLDING_TYPE_UNKNOWN:
    "this entity's type isn't configured, so governance roles can't be assigned",
  GOVERNING_ORGAN_HAS_NO_SEAT:
    'this entity is governed by a body that has no assignable role yet',
  GOVERNANCE_ROLE_NOT_ALLOWED_FOR_HOLDING_TYPE:
    'that governance role is not available for this entity type',
  REQUIRED_VALUE: 'a required field is missing a value',
  TYPE_MISMATCH: 'a field value has the wrong format',
  VALUE_TOO_LONG: 'a text value is too long',
  COMPANY_MISMATCH: 'a selected file or address is not in this workspace',
  FORBIDDEN: 'you do not have access to that entity',
  CORPORATION_NOT_IN_COMPANY: 'that entity is not in this workspace',
};
